/**
 * Перетаскивание плавающих окон (фидбек владельца 26.07): панель таскается
 * за шапку (pointer events, мышь и палец), позиция запоминается на устройстве
 * (localStorage drag:{key}) и восстанавливается при следующем открытии.
 * Порог 6px отличает перетаскивание от клика (wasDragged — для кнопок-шапок).
 * Панель не может уехать за экран (клампы при драге и при восстановлении).
 */
import { useEffect, useRef, useState } from 'preact/hooks'

interface DragPos {
  x: number
  y: number
}

/** Минимум панели, остающийся на экране (px) — за него можно ухватиться. */
const KEEP_VISIBLE = 56
const CLICK_SLOP = 6

function storageKeyOf(key: string): string {
  return `drag:${key}`
}

function loadPos(key: string): DragPos | null {
  try {
    const raw = localStorage.getItem(storageKeyOf(key))
    if (!raw) return null
    const p = JSON.parse(raw) as DragPos
    return Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null
  } catch {
    return null
  }
}

function savePos(key: string, pos: DragPos | null): void {
  try {
    if (pos) localStorage.setItem(storageKeyOf(key), JSON.stringify(pos))
    else localStorage.removeItem(storageKeyOf(key))
  } catch {
    // приватный режим — позиция не сохраняется
  }
}

export interface DragPanel {
  /** Ставится на корень панели. */
  elRef: (el: HTMLElement | null) => void
  /** style корня: transform-сдвиг от CSS-позиции по умолчанию. */
  style: { transform: string } | undefined
  /** Ставится на шапку-ручку (onPointerDown). */
  onDragStart: (e: PointerEvent) => void
  /** true, если последний pointer-жест был перетаскиванием (гасить клик). */
  wasDragged: () => boolean
}

export function useDragPanel(key: string): DragPanel {
  const [pos, setPos] = useState<DragPos | null>(() => loadPos(key))
  const el = useRef<HTMLElement | null>(null)
  const moved = useRef(false)
  const posRef = useRef<DragPos | null>(pos)
  posRef.current = pos

  // Восстановленная позиция может не влезать в текущий вьюпорт — сброс.
  useEffect(() => {
    const node = el.current
    if (!node || !posRef.current) return
    const r = node.getBoundingClientRect()
    const off =
      r.right < KEEP_VISIBLE ||
      r.left > window.innerWidth - KEEP_VISIBLE ||
      r.top < 0 ||
      r.top > window.innerHeight - KEEP_VISIBLE
    if (off) {
      setPos(null)
      savePos(key, null)
    }
  }, [key])

  const onDragStart = (e: PointerEvent): void => {
    const node = el.current
    if (!node) return
    const base = posRef.current ?? { x: 0, y: 0 }
    const start = { x: e.clientX, y: e.clientY }
    const rect0 = node.getBoundingClientRect()
    moved.current = false

    const clamp = (p: DragPos): DragPos => {
      const left = rect0.left + (p.x - base.x)
      const top = rect0.top + (p.y - base.y)
      const clampedLeft = Math.min(
        Math.max(left, KEEP_VISIBLE - rect0.width),
        window.innerWidth - KEEP_VISIBLE,
      )
      const clampedTop = Math.min(Math.max(top, 0), window.innerHeight - KEEP_VISIBLE)
      return { x: p.x + (clampedLeft - left), y: p.y + (clampedTop - top) }
    }

    const onMove = (ev: PointerEvent): void => {
      const dx = ev.clientX - start.x
      const dy = ev.clientY - start.y
      if (!moved.current && Math.hypot(dx, dy) < CLICK_SLOP) return
      moved.current = true
      setPos(clamp({ x: base.x + dx, y: base.y + dy }))
    }
    const onUp = (): void => {
      window.removeEventListener('pointermove', onMove)
      if (moved.current) savePos(key, posRef.current)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp, { once: true })
  }

  return {
    elRef: (node) => {
      el.current = node
    },
    style: pos ? { transform: `translate(${pos.x}px, ${pos.y}px)` } : undefined,
    onDragStart,
    wasDragged: () => moved.current,
  }
}
